import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Subject, of, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';

import { AlertEventState, AlertSeverityLevel } from 'src/app/enums/alert.enum';
import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import { AppNotification } from 'src/app/models/notification';
import { NotificationCenterItem } from 'src/app/models/notification-center-item.model';
import { AuthService } from 'src/app/services/auth.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastService } from 'src/app/services/toast.service';
import { trackById } from 'src/app/shared/utils/track-by.util';
import { toTimestampMs } from 'src/app/shared/utils/timestamp.util';
import { buildAlertGraphWindowMs } from 'src/app/shared/utils/alert-graph-window.util';

@Component({
  standalone: false,
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  unreadCount = 0;
  displayCount = '';
  suppressedLastHourCount = 0;
  topUnacknowledgedAlerts: NotificationCenterItem[] = [];
  isPanelOpen = false;

  readonly trackById = trackById;

  private readonly destroy$ = new Subject<void>();
  private readonly pendingAckIds = new Set<string>();

  constructor(
    private readonly notificationService: NotificationService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly dateFormatService: DateFormatService,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    timer(0, 15000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          if (!this.authService.getAccessToken()) {
            return EMPTY;
          }

          return this.notificationService.getActiveAssignedAlertEvents().pipe(
            catchError((error: unknown) => {
              if (!this.isSessionFailure(error)) {
                this.toastService.showError(error);
              }
              return of([] as AppNotification[]);
            }),
          );
        }),
      )
      .subscribe((alerts) => {
        this.applyAlerts(alerts);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  togglePanel(): void {
    this.isPanelOpen = !this.isPanelOpen;
  }

  closePanel(): void {
    this.isPanelOpen = false;
  }

  openAlertDetails(alert: NotificationCenterItem): void {
    this.closePanel();

    if (!alert.nodeId) {
      this.router.navigate(['/dashboard/notifications'], {
        queryParams: { alertEventId: alert.id },
      });
      return;
    }

    const centerMs = this.toTimestampMs(alert.startedAt)
      ?? this.toTimestampMs(alert.sentAt)
      ?? Date.now();

    const { startMs, endMs } = buildAlertGraphWindowMs(centerMs);

    const queryParams: Record<string, string | number> = {
      alertEventId: alert.id,
      dateFilterId: DateFilterTypeEnum.Custom,
      startDate: Math.floor(startMs / 1000),
      endDate: Math.floor(endMs / 1000),
    };

    if (alert.organizationLevelId) {
      queryParams['selectedOrgId'] = alert.organizationLevelId;
    }

    if (alert.parameterId) {
      queryParams['selectedParameterId'] = alert.parameterId;
    }

    this.router.navigate(['/dashboard/node-detail', alert.nodeId], { queryParams });
  }

  openNotificationList(): void {
    this.closePanel();
    this.router.navigate(['/dashboard/notifications']);
  }

  acknowledgeAlert(event: MouseEvent, alertId: string): void {
    event.stopPropagation();

    this.pendingAckIds.add(alertId);
    this.notificationService.acknowledgeAlertEvent(alertId).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: () => {
        this.toastService.showSuccess('Alert acknowledged successfully', true);
        this.softenAcknowledgedAlert(alertId);
      },
      error: (error) => {
        this.pendingAckIds.delete(alertId);
        this.toastService.showError(error);
      },
    });
  }

  // Stay and Soften (DASH-2231/DASH-3250): the row stays in the dropdown, visually
  // softened, instead of disappearing the instant it is acknowledged. It drops out
  // naturally on the next poll once the server reports it as no longer unacknowledged.
  // The id stays in pendingAckIds so that poll can't resurrect it if the BE hasn't
  // caught up yet.
  private softenAcknowledgedAlert(alertId: string): void {
    this.topUnacknowledgedAlerts = this.topUnacknowledgedAlerts.map((alert) =>
      alert.id === alertId ? { ...alert, isAcknowledged: true } : alert,
    );
    this.unreadCount = Math.max(0, this.unreadCount - 1);
    this.displayCount = this.unreadCount > 99 ? '99+' : this.unreadCount.toString();
    this.cdr.markForCheck();
  }

  private applyAlerts(alerts: AppNotification[]): void {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    this.suppressedLastHourCount = alerts.filter((alert) =>
      alert.isSuppressed && this.getTimestamp(alert) >= oneHourAgo,
    ).length;

    const unacknowledged = alerts
      .filter((alert) => this.isUnacknowledged(alert.state) && !this.pendingAckIds.has(alert.id))
      .sort((a, b) => {
        const severityDiff = (b.severity ?? AlertSeverityLevel.Watch) - (a.severity ?? AlertSeverityLevel.Watch);
        if (severityDiff !== 0) {
          return severityDiff;
        }

        return this.getTimestamp(b) - this.getTimestamp(a);
      });

    this.unreadCount = unacknowledged.length;
    this.displayCount = this.unreadCount > 99 ? '99+' : this.unreadCount.toString();

    this.topUnacknowledgedAlerts = unacknowledged
      .slice(0, 10)
      .map((alert) => this.mapToCenterItem(alert));

  }

  private mapToCenterItem(alert: AppNotification): NotificationCenterItem {
    const severity = alert.severity ?? AlertSeverityLevel.Watch;
    const site = alert.siteName || alert.endPoint || alert.notificationName || 'N/A';
    const sensor = alert.sensorId || alert.sensor || '-';
    const value = alert.peakValue ?? alert.value;
    const parameterName =
      alert.parameterName ||
      alert.notificationName ||
      (alert.parameterId != null ? `Parameter #${alert.parameterId}` : 'N/A');

    return {
      id: alert.id,
      parameterName,
      valueDisplay: value !== undefined && value !== null ? `${value}` : 'N/A',
      siteSensorDisplay: `${site} / ${sensor}`,
      eventTime: alert.startedAt || alert.sentDate || alert.notificationSentDate || Date.now(),
      nodeId: alert.endPointId ?? null,
      organizationLevelId: alert.organizationLevelId ?? null,
      parameterId: alert.parameterId ?? null,
      startedAt: alert.startedAt,
      endedAt: alert.endedAt ?? undefined,
      sentAt: alert.notificationSentDate || alert.sentDate,
      severity,
      shouldPulse: severity === AlertSeverityLevel.Critical || severity === AlertSeverityLevel.Alert,
      isAcknowledged: false,
    };
  }

  // The interceptor owns 401 handling (refresh or logout); a background poll
  // that loses its session should go quiet rather than toast on every tick.
  private isSessionFailure(error: unknown): boolean {
    return (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized)
      || !this.authService.getAccessToken();
  }

  private isUnacknowledged(state: AlertEventState | undefined): boolean {
    return state === AlertEventState.Active || state === AlertEventState.Escalated;
  }

  private getTimestamp(alert: AppNotification): number {
    const alertDate = alert.startedAt || alert.sentDate || alert.notificationSentDate;
    if (!alertDate) {
      return 0;
    }

    const timestamp = new Date(alertDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private toTimestampMs(value: Date | string | number | null | undefined): number | null {
    return toTimestampMs(value, this.dateFormatService);
  }
}

